import { useState, useEffect, useCallback } from 'react';
import type { LLMRequestInfo, TokenUsage } from '../types';
import { chatWithLLM, fetchChatSystemPrompt, saveChatSystemPrompt } from '../services/api';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import './ChatPage.css';

interface Props {
  onBack: () => void;
  onNavigateToModelSettings?: () => void;
  onNavigateToKon?: () => void;
  showDebugInfo?: boolean;
  language?: Language;
}

export default function ChatPage({ onBack, onNavigateToModelSettings, onNavigateToKon, showDebugInfo, language = 'en' }: Props) {
  const [message, setMessage] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [savedSystemPrompt, setSavedSystemPrompt] = useState('');
  const [systemPromptSaved, setSystemPromptSaved] = useState(false);

  const [responseText, setResponseText] = useState('');
  const [llmRequest, setLlmRequest] = useState<LLMRequestInfo | null>(null);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const prompt = await fetchChatSystemPrompt();
        if (cancelled) return;
        if (prompt) {
          setSystemPrompt(prompt);
          setSavedSystemPrompt(prompt);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSend = useCallback(async () => {
    if (!message.trim() || loading) return;
    setLoading(true);
    setError('');
    setResponseText('');
    setLlmRequest(null);
    setTokenUsage(null);

    try {
      const result = await chatWithLLM(
        message.trim(),
        systemPrompt.trim() || undefined,
      );
      setResponseText(result.text);
      if (result.llmRequest) setLlmRequest(result.llmRequest);
      if (result.tokenUsage) setTokenUsage(result.tokenUsage);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [message, systemPrompt, loading]);

  const handleSaveSystemPrompt = useCallback(async () => {
    try {
      await saveChatSystemPrompt(systemPrompt);
      setSavedSystemPrompt(systemPrompt);
      setSystemPromptSaved(true);
      setTimeout(() => setSystemPromptSaved(false), 2000);
    } catch {
      // ignore
    }
  }, [systemPrompt]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const modelSettingsNav = (
    <nav className="nav-links">
      <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>{t(language, 'kon.backToMain')}</a>
      {onNavigateToKon && <a href="/kon/" onClick={(e) => { e.preventDefault(); onNavigateToKon(); }}>{t(language, 'nav.koniugacja')}</a>}
      {onNavigateToModelSettings && (
        <a href="/settings" className="nav-settings-link" onClick={(e) => { e.preventDefault(); onNavigateToModelSettings(); }}>
          {t(language, 'nav.settings')}
        </a>
      )}
    </nav>
  );

  if (initialLoading) {
    return (
      <div className="chat-page">
        <header>
          <h1>{t(language, 'chat.title')}</h1>
          {modelSettingsNav}
        </header>
        <div className="loading"><div className="spinner" /><span>Loading…</span></div>
      </div>
    );
  }

  const systemPromptChanged = systemPrompt !== savedSystemPrompt;

  return (
    <div className="chat-page">
      <header>
        <h1>{t(language, 'chat.title')}</h1>
        {modelSettingsNav}
      </header>

      <div className="chat-sections">
        {/* System Prompt Section */}
        <section className="chat-section">
          <h2>🔧 System Prompt</h2>
          <div className="field">
            <textarea
              rows={4}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Optional system prompt (e.g. 'You are a helpful Polish language tutor')"
            />
          </div>
          <div className="system-prompt-actions">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSaveSystemPrompt}
              disabled={!systemPromptChanged}
            >
              💾 Save System Prompt
            </button>
            {systemPromptSaved && (
              <span className="system-prompt-saved">✓ Saved</span>
            )}
          </div>
        </section>

        {/* Message Input Section */}
        <section className="chat-section">
          <h2>✉️ Message</h2>
          <div className="chat-input-row">
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message… (Ctrl+Enter to send)"
              disabled={loading}
            />
            <button
              className="btn btn-primary"
              onClick={handleSend}
              disabled={!message.trim() || loading}
            >
              {loading ? '⏳' : '🚀 Send'}
            </button>
          </div>
          {error && <div className="chat-error">❌ {error}</div>}
        </section>

        {/* Response Section */}
        {(responseText || loading) && (
          <section className="chat-section">
            <h2>📝 Response</h2>
            {loading ? (
              <div className="loading"><div className="spinner" /><span>Generating…</span></div>
            ) : (
              <>
                <div className="chat-response-text">{responseText}</div>

                {/* Debug Info */}
                {showDebugInfo && llmRequest && (
                  <details className="chat-debug">
                    <summary>🔍 Debug Info</summary>
                    <div className="chat-debug-content">
                      <div className="chat-debug-row">
                        <span className="chat-debug-label">Provider:</span>
                        <span className="chat-debug-value">{llmRequest.provider}</span>
                      </div>
                      <div className="chat-debug-row">
                        <span className="chat-debug-label">Model:</span>
                        <span className="chat-debug-value">{llmRequest.model}</span>
                      </div>
                      <div className="chat-debug-row">
                        <span className="chat-debug-label">Max Tokens:</span>
                        <span className="chat-debug-value">{llmRequest.maxTokens}</span>
                      </div>
                      <div className="chat-debug-row">
                        <span className="chat-debug-label">Temperature:</span>
                        <span className="chat-debug-value">{llmRequest.temperature}</span>
                      </div>
                      {llmRequest.topP !== undefined && (
                        <div className="chat-debug-row">
                          <span className="chat-debug-label">Top P:</span>
                          <span className="chat-debug-value">{llmRequest.topP}</span>
                        </div>
                      )}
                      {llmRequest.topK !== undefined && (
                        <div className="chat-debug-row">
                          <span className="chat-debug-label">Top K:</span>
                          <span className="chat-debug-value">{llmRequest.topK}</span>
                        </div>
                      )}
                      {llmRequest.thinkingBudget !== undefined && (
                        <div className="chat-debug-row">
                          <span className="chat-debug-label">Thinking:</span>
                          <span className="chat-debug-value">{llmRequest.thinkingBudget} tokens</span>
                        </div>
                      )}
                      {llmRequest.thinkingLevel !== undefined && (
                        <div className="chat-debug-row">
                          <span className="chat-debug-label">Thinking:</span>
                          <span className="chat-debug-value">{llmRequest.thinkingLevel}</span>
                        </div>
                      )}
                      <div className="chat-debug-row" style={{ marginTop: 8 }}>
                        <span className="chat-debug-label">Prompt:</span>
                      </div>
                      <pre>{llmRequest.prompt}</pre>

                      {tokenUsage && (
                        <>
                          <div className="chat-debug-row" style={{ marginTop: 12 }}>
                            <span className="chat-debug-label">Input Tokens:</span>
                            <span className="chat-debug-value">{tokenUsage.inputTokens}</span>
                          </div>
                          <div className="chat-debug-row">
                            <span className="chat-debug-label">Output Tokens:</span>
                            <span className="chat-debug-value">{tokenUsage.outputTokens}</span>
                          </div>
                          {tokenUsage.thinkingTokens !== undefined && tokenUsage.thinkingTokens > 0 && (
                            <div className="chat-debug-row">
                              <span className="chat-debug-label">Thinking:</span>
                              <span className="chat-debug-value">{tokenUsage.thinkingTokens} tokens</span>
                            </div>
                          )}
                          <div className="chat-debug-row">
                            <span className="chat-debug-label">Total Tokens:</span>
                            <span className="chat-debug-value">{tokenUsage.totalTokens}</span>
                          </div>
                          {tokenUsage.estimatedCost && (
                            <div className="chat-debug-row">
                              <span className="chat-debug-label">Est. Cost:</span>
                              <span className="chat-debug-value">
                                ${tokenUsage.estimatedCost.totalCost.toFixed(6)} {tokenUsage.estimatedCost.currency}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </details>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
